import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError, switchMap, filter, take, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoginRequestDto, LoginResponseDto } from '../models/login.dto';
import { CollaborativeSignupRequestDto, SignupRequestDto, SignupResponseDto } from '../models/signup.dto';
import { ConfirmEmailResponseDto } from '../models/confirm-email.dto';
import { InvitationPreviewResponseDto } from '../models/invitation-preview.dto';
import { ResendVerificationEmailResponseDto } from '../models/resend-verification-email.dto';
import {
  RecoverPasswordBodyDto,
  RecoverPasswordResponseDto,
  RecoverPasswordVerifyResponseDto,
  ResetPasswordBodyDto,
  ResetPasswordResponseDto,
  VerifyRecoverPasswordBodyDto,
} from '../models/recover-password.dto';
import { ProjectService } from '../../../shared/services/project.service';
import { DashboardService } from '../../../features/dashboard/services/dashboard.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { SessionService } from '../../../shared/services/session.service';
import { StorageService } from '../../../shared/services/storage.service';
import { STORAGE_KEYS } from '../../../shared/services/storage-keys';

interface TokenPayload {
  exp: number;
  iat?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly projectService = inject(ProjectService);
  private readonly dashboardService = inject(DashboardService);
  private readonly notificationService = inject(NotificationService);
  private readonly storage = inject(StorageService);

  private readonly apiUrl = `${environment.apiHost}/api`;
  private readonly tokenRefreshThreshold = 5 * 60 * 1000; // 5 minutes before expiration

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  login(credentials: LoginRequestDto): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(`${this.apiUrl}/auth/login`, credentials).pipe(
      tap((response) => {
        if (response.status === 'success') {
          this.saveTokens(response.data.access, response.data.refresh);
        }
      }),
    );
  }

  signup(credentials: SignupRequestDto): Observable<SignupResponseDto> {
    return this.http.post<SignupResponseDto>(`${this.apiUrl}/users/signup/`, credentials);
  }

  /**
   * Collaborative signup: POST /api/users/collaborative-signup/.
   * Same response shape as signup; backend sets email_verified = true, so no verify-email step is needed.
   */
  collaborativeSignup(body: CollaborativeSignupRequestDto): Observable<SignupResponseDto> {
    return this.http.post<SignupResponseDto>(`${this.apiUrl}/users/collaborative-signup/`, body);
  }

  /**
   * Preferences deliberately survive: they are per-device choices with no server behind them.
   * This used to also wipe the user's saved request filters, which defended against one account
   * inheriting another's on a shared browser — the job issue `session-state/06` gives to
   * user-scoped keys instead. It also keyed that wipe on the user id, so it leaked those keys
   * permanently whenever identity had never loaded.
   */
  logout(): void {
    this.clearTokens();
    this.session.clear();
    this.projectService.clearProject();
    this.dashboardService.clearCache();
    this.notificationService.reset();
    this.router.navigate(['/auth/login']);
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  saveTokens(access: string, refresh: string): void {
    access && this.storage.setString(STORAGE_KEYS.auth.access, access);
    refresh && this.storage.setString(STORAGE_KEYS.auth.refresh, refresh);
    this.refreshTokenSubject.next(access);
  }

  getAccessToken(): string | null {
    return this.storage.getString(STORAGE_KEYS.auth.access);
  }

  getRefreshToken(): string | null {
    return this.storage.getString(STORAGE_KEYS.auth.refresh);
  }

  clearTokens(): void {
    this.storage.remove(STORAGE_KEYS.auth.access);
    this.storage.remove(STORAGE_KEYS.auth.refresh);
    this.refreshTokenSubject.next(null);
  }

  // ============================================
  // EMAIL VERIFICATION (WALL)
  // ============================================

  /**
   * True when the user counts as verified, i.e. the wall does not apply.
   *
   * The JWT is the only record consulted: every access token, including the one
   * `/auth/refresh/` hands back, carries `email_verified` with the value currently in the DB.
   * A verification that happened elsewhere therefore only lands here once the token is
   * refreshed — `checkEmailVerified$()` is what does that.
   */
  isEmailVerified(): boolean {
    if (!this.hasValidToken()) {
      return true; // No token => not applicable
    }
    const payload = this.getTokenPayload();
    if (payload && typeof payload['email_verified'] === 'boolean') {
      return payload['email_verified'];
    }
    return false;
  }

  /**
   * Ask the server whether the email is verified by now: refresh the token, then read the
   * flag off the fresh one. This is the only way a confirmation made in another tab, or from
   * the link in the email, reaches a session that started out unverified.
   */
  checkEmailVerified$(): Observable<boolean> {
    return this.tryRefreshToken().pipe(map(() => this.isEmailVerified()));
  }

  resendVerificationEmail(): Observable<ResendVerificationEmailResponseDto> {
    return this.http.post<ResendVerificationEmailResponseDto>(`${this.apiUrl}/users/resend-verification-email/`, {});
  }

  // ============================================
  // TOKEN VALIDATION
  // ============================================

  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.getAccessToken();
    if (!tokenToCheck) {
      return true;
    }

    try {
      const payload = this.decodeToken(tokenToCheck);
      if (!payload || !payload.exp) {
        return true;
      }

      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      return now >= expirationTime;
    } catch (error) {
      return true;
    }
  }

  isTokenNearExpiration(token?: string): boolean {
    const tokenToCheck = token || this.getAccessToken();
    if (!tokenToCheck) {
      return true;
    }

    try {
      const payload = this.decodeToken(tokenToCheck);
      if (!payload || !payload.exp) {
        return true;
      }

      const expirationTime = payload.exp * 1000;
      const now = Date.now();
      const timeUntilExpiration = expirationTime - now;

      return timeUntilExpiration <= this.tokenRefreshThreshold;
    } catch (error) {
      return true;
    }
  }

  hasValidToken(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired(token);
  }

  // ============================================
  // TOKEN REFRESH
  // ============================================

  refreshAccessToken(): Observable<string> {
    if (this.isRefreshing) {
      // If already refreshing, return the existing observable
      return this.refreshTokenSubject.asObservable().pipe(
        filter((token): token is string => token !== null),
        take(1),
      );
    }

    this.isRefreshing = true;
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.isRefreshing = false;
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<LoginResponseDto>(`${this.apiUrl}/auth/refresh/`, {
        refresh: refreshToken,
      })
      .pipe(
        tap((response) => {
          if (response.status === 'success') {
            this.saveTokens(response.data.access, response.data.refresh);
            this.refreshTokenSubject.next(response.data.access);
            this.isRefreshing = false;
          } else {
            this.isRefreshing = false;
            this.logout();
          }
        }),
        switchMap((response) => {
          if (response.status === 'success') {
            return new Observable<string>((subscriber) => {
              subscriber.next(response.data.access);
              subscriber.complete();
            });
          }
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.logout();
          return throwError(() => error);
        }),
      );
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  private decodeToken(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        return null;
      }
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  getTokenPayload(): TokenPayload | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    return this.decodeToken(token);
  }

  /**
   * Attempt to refresh the access token without logout side-effects.
   * Used by the confirm-email flow where logout on failure is undesirable.
   */
  tryRefreshToken(): Observable<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return of(false);
    }
    return this.http
      .post<LoginResponseDto>(`${this.apiUrl}/auth/refresh`, {
        refresh: refreshToken,
      })
      .pipe(
        switchMap((response) => {
          const { access, refresh } = response.data || {};
          if (response.status === 'success') {
            this.saveTokens(access, refresh);
          }
          return of(response.status === 'success');
        }),
        catchError((err) => {
          return of(false);
        }),
      );
  }

  // ============================================
  // CONFIRM EMAIL METHODS
  // ============================================
  confirmEmail(token: string): Observable<ConfirmEmailResponseDto> {
    return this.http.get<ConfirmEmailResponseDto>(`${this.apiUrl}/users/confirm-email?id=${token}`);
  }

  /**
   * Public preview of an org invitation by token (for the invite-link landing on signup).
   * GET /api/organizations/invitations/by-token/?token=
   */
  getInvitationPreview(token: string): Observable<InvitationPreviewResponseDto> {
    return this.http.get<InvitationPreviewResponseDto>(
      `${this.apiUrl}/organizations/invitations/by-token/?token=${encodeURIComponent(token)}`,
    );
  }

  // ============================================
  // RECOVER PASSWORD METHODS
  // ============================================
  recoverPassword(body: RecoverPasswordBodyDto): Observable<RecoverPasswordResponseDto> {
    return this.http.post<RecoverPasswordResponseDto>(`${this.apiUrl}/users/recover-password/`, body);
  }

  verifyRecoverPassword(body: VerifyRecoverPasswordBodyDto): Observable<RecoverPasswordVerifyResponseDto> {
    return this.http.post<RecoverPasswordVerifyResponseDto>(
      `${this.apiUrl}/users/validate-recover-password-code/`,
      body,
    );
  }

  resetPassword(body: ResetPasswordBodyDto): Observable<ResetPasswordResponseDto> {
    return this.http.post<ResetPasswordResponseDto>(`${this.apiUrl}/users/reset-password/`, body);
  }
}
